import { Box, Flex, Grid, Skeleton, Text } from '@radix-ui/themes';
import { CommonCallout } from 'components/common/callouts';
import { SuperAdminIcon } from 'components/common/custom-icon/shorthands';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonSliderInput } from 'components/common/form/slider';
import { Blinker } from 'components/machine/camera-stream-viewer/blinker';
import { format } from 'date-fns-tz';
import { t } from 'i18next';
import { LOCAL_TIMEZONE } from 'lib_ts/enums/datetime.enums';
import { CameraSource } from 'lib_ts/enums/machine-msg.enum';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useAuthStore } from 'stores/use-auth-store';
import { StreamingContext } from './context';
import { Crosshair } from './crosshair';
import './index.scss';

const TOGGLE_PLAY_KEY = 'Space';

interface IProps {
  machineID: string;

  // for the skeleton while loading
  loadingHeight?: string;

  hideSource?: boolean;
  hideStreamFailed?: boolean;
  hideRefreshRate?: boolean;
  hideLastUpdated?: boolean;

  // provide to show only one side of a stereo stream
  splitViewSide?: 'left' | 'right';

  allowCrosshair?: boolean;
}

const AdminInputs = (props: {
  hideSource?: boolean;
  hideRefreshRate?: boolean;
}) => {
  const { sourceOptions, source, refreshRate, setSource, setRefreshRate } =
    useContext(StreamingContext);

  if (props.hideSource && props.hideRefreshRate) {
    return null;
  }

  const gridColCt = props.hideSource ? '1' : '2';

  return (
    <Grid columns={gridColCt} gap={RADIX.FLEX.GAP.MD}>
      {!props.hideSource && (
        <CommonSelectInput
          id="ms-inspector-source"
          name="source"
          label="Source"
          labelIcon={<SuperAdminIcon />}
          options={sourceOptions}
          value={source}
          onChange={(v) => setSource(v as CameraSource)}
        />
      )}

      {!props.hideRefreshRate && (
        <CommonSliderInput
          id="ms-inspector-refresh-rate-slider"
          name="refresh-rate"
          label={`Refresh Rate (${refreshRate} / sec)`}
          labelIcon={<SuperAdminIcon />}
          value={refreshRate}
          min={1}
          max={48}
          step={1}
          onChange={(v) => setRefreshRate(+v)}
          stepperLabel="Refresh Rate"
        />
      )}
    </Grid>
  );
};

export const MachineCameraStreamViewer = (props: IProps) => {
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);

  const { lastFrame, streamFailed, retryStream, togglePaused } =
    useContext(StreamingContext);

  // toggle playback w/ space
  useEffect(() => {
    const callback = (event: KeyboardEvent) => {
      if (![TOGGLE_PLAY_KEY].includes(event.code)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (event.repeat) {
        return;
      }

      switch (event.code) {
        case TOGGLE_PLAY_KEY: {
          togglePaused();
          return;
        }

        default: {
          return;
        }
      }
    };

    document.addEventListener('keydown', callback);
    return () => document.removeEventListener('keydown', callback);
  }, []);

  // update the src of the <img> tag from buffer of last frame
  const [blob, setBlob] = useState<Blob>();
  const [aspectRatio, setAspectRatio] = useState<string>();

  const imgSrc = useMemo(() => {
    if (!blob) {
      return;
    }

    return window.URL.createObjectURL(blob);
  }, [blob]);

  useEffect(() => {
    if (!lastFrame?.buffer) {
      return;
    }

    const buffer = new Uint8Array(lastFrame.buffer);
    const blob = new Blob([buffer.buffer]);
    setBlob(blob);
  }, [lastFrame]);

  useEffect(() => {
    if (aspectRatio) {
      // stop checking this once we've determined it 1x
      return;
    }

    if (!imgSrc) {
      return;
    }

    const img = new Image();

    img.onload = () => {
      setAspectRatio((img.width / (img.height || 1)).toFixed(2));
    };

    img.src = imgSrc; // load the image to trigger onload
  }, [imgSrc, aspectRatio]);

  return (
    <ErrorBoundary identifier="MachineCameraStreamViewer">
      <Flex direction="column" gap={RADIX.FLEX.GAP.MD} className="CameraStream">
        {userRoleFeatureFlags.controls.cameraSources && (
          <AdminInputs
            hideSource={props.hideSource}
            hideRefreshRate={props.hideRefreshRate}
          />
        )}

        {streamFailed && !props.hideStreamFailed && (
          <CommonCallout
            color={RADIX.COLOR.WARNING}
            text={t('common.unable-to-stream').toString()}
            actions={[
              {
                label: 'Try Again',
                onClick: retryStream,
              },
            ]}
          />
        )}
        <Skeleton height={props.loadingHeight ?? '300px'} loading={!imgSrc}>
          <Flex className="sizer">
            {/* wrapper for a sprite-like view for left/right side of stereo camera */}
            <Box
              className={
                props.splitViewSide
                  ? `split-view split-view-${props.splitViewSide}`
                  : ''
              }
              style={{
                aspectRatio: props.splitViewSide
                  ? `${aspectRatio} / 2`
                  : undefined,
              }}
            >
              <Blinker />

              <img
                src={imgSrc}
                style={{ aspectRatio: aspectRatio }}
                className="camera-feed-wide select-none"
                alt={`${lastFrame?.source ?? 'unknown'} @ ${
                  lastFrame?.timestamp ?? 'unknown'
                }`}
              />

              {props.allowCrosshair && imgSrc && <Crosshair />}

              {!props.hideLastUpdated && lastFrame?.timestamp && (
                <div className="timestamp">
                  <Text size={RADIX.TEXT.SIZE.XS}>
                    {t('common.last-updated')}:{' '}
                    {format(
                      new Date(lastFrame.timestamp),
                      'yyyy-MM-dd @ hh:mm:ss a',
                      { timeZone: LOCAL_TIMEZONE }
                    )}
                  </Text>
                </div>
              )}
            </Box>
          </Flex>
        </Skeleton>
      </Flex>
    </ErrorBoundary>
  );
};
